import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { userAPI } from "../services/api";
import { LEAD_STAGE, leadStageToRouteMap } from "../Utils/constants";
import useLeadStage from "../hooks/useLeadStage";
import Toast from "../components/Toast";

// This page is ONLY for GC SSO partially-registered leads.
const curr_page_lead_stage = [LEAD_STAGE.PARTIAL_REGISTERED_LEAD];

export default function PartialEmployment() {
  const navigate = useNavigate();
  const { leadStage, isLoadingStage } = useLeadStage();

  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "error" });

  const [formData, setFormData] = useState({
    companyName: "",
    salaryDate: "",
    officialEmail: "",
  });

  const [errors, setErrors] = useState({});
  const [userLocation, setUserLocation] = useState({ lat: null, lng: null });

  // Best-effort geolocation (optional - saved in the backend if available).
  useEffect(() => {
    let isMounted = true;
    const stored = sessionStorage.getItem("gcGeoLocation");
    if (stored) {
      try {
        const geo = JSON.parse(stored);
        if (geo?.lat && geo?.lng) setUserLocation({ lat: geo.lat, lng: geo.lng });
      } catch {
        /* ignore */
      }
    }
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (isMounted) {
          setUserLocation({
            lat: pos.coords.latitude.toString(),
            lng: pos.coords.longitude.toString(),
          });
        }
      },
      () => {},
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
    return () => {
      isMounted = false;
    };
  }, []);

  // Redirect if the lead is not in the partial-registration stage.
  useEffect(() => {
    if (leadStage && !isLoadingStage && !curr_page_lead_stage.includes(leadStage)) {
      navigate(leadStageToRouteMap[leadStage] || "/dashboard");
    }
  }, [leadStage, isLoadingStage, navigate]);

  const showToast = (message, type = "error") => setToast({ show: true, message, type });
  const hideToast = () => setToast({ show: false, message: "", type: "error" });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;
    if (name === "companyName") {
      processedValue = value.slice(0, 50);
    }
    setFormData((prev) => ({ ...prev, [name]: processedValue }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.companyName.trim()) {
      newErrors.companyName = "Company name is required";
    }
    if (!formData.salaryDate) {
      newErrors.salaryDate = "Previous salary date is required";
    }
    if (!formData.officialEmail.trim()) {
      newErrors.officialEmail = "Official email is required";
    } else if (!emailRegex.test(formData.officialEmail.trim())) {
      newErrors.officialEmail = "Please enter a valid email address";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    hideToast();

    const formErrors = validateForm();
    setErrors(formErrors);
    if (Object.keys(formErrors).length > 0) {
      showToast(Object.values(formErrors)[0]);
      return;
    }

    setIsLoading(true);
    try {
      const data = {
        company_name: formData.companyName.trim(),
        salary_date: formData.salaryDate,
        official_email: formData.officialEmail.trim().toLowerCase(),
        lat: userLocation.lat && userLocation.lng ? userLocation.lat : null,
        lng: userLocation.lat && userLocation.lng ? userLocation.lng : null,
      };
      const response = await userAPI.fillPartialEmploymentDetails(data);
      if (response) {
        navigate("/apply/account-aggregator");
      }
    } catch (error) {
      showToast(error?.message || "Failed to submit. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoadingStage || !leadStage) {
    return (
      <section className="w-full min-h-[calc(100vh-160px)] flex items-center justify-center bg-gradient-to-br from-[#F8FAFC] via-[#FFFFFF] to-[#F1F5F9]">
        <div className="flex flex-col items-center gap-4">
          <svg className="animate-spin h-10 w-10 text-[#FF6B35]" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <p className="text-[#64748B] text-sm font-medium">Loading...</p>
        </div>
      </section>
    );
  }

  return (
    <section className="w-full min-h-[calc(100vh-160px)] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#F8FAFC] via-[#FFFFFF] to-[#F1F5F9]">
      {toast.show && (
        <Toast message={toast.message} type={toast.type} onClose={hideToast} />
      )}

      <div className="w-full max-w-lg">
        <div className="bg-white rounded-3xl shadow-2xl shadow-[#FF6B35]/10 border border-[#E2E8F0] p-8 sm:p-10">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#FF6B35]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-[#FF6B35]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#1E3A8A] mb-2">
              A few more details
            </h1>
            <p className="text-[#64748B] text-sm sm:text-base">
              Tell us about your employment to continue
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Company Name */}
            <div>
              <label className="block text-sm font-semibold text-[#1E293B] mb-2">
                Company Name
              </label>
              <input
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleInputChange}
                placeholder="Enter your company name"
                maxLength={50}
                className={`w-full px-4 py-3.5 bg-white border ${errors.companyName ? "border-red-400 ring-1 ring-red-200" : "border-[#E2E8F0] focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/20"} rounded-xl text-[#1E293B] placeholder:text-[#94A3B8] text-sm font-medium transition-all focus:outline-none`}
              />
              {errors.companyName && (
                <p className="text-red-500 text-xs mt-1.5 ml-1">{errors.companyName}</p>
              )}
            </div>

            {/* Salary Date */}
            <div>
              <label className="block text-sm font-semibold text-[#1E293B] mb-2">
                Previous Salary Date
              </label>
              <input
                type="date"
                name="salaryDate"
                value={formData.salaryDate}
                onChange={handleInputChange}
                max={new Date().toISOString().split("T")[0]}
                className={`w-full px-4 py-3.5 bg-white border ${errors.salaryDate ? "border-red-400 ring-1 ring-red-200" : "border-[#E2E8F0] focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/20"} rounded-xl text-[#1E293B] text-sm font-medium transition-all focus:outline-none`}
              />
              {errors.salaryDate && (
                <p className="text-red-500 text-xs mt-1.5 ml-1">{errors.salaryDate}</p>
              )}
            </div>

            {/* Official Email */}
            <div>
              <label className="block text-sm font-semibold text-[#1E293B] mb-2">
                Official Email
              </label>
              <input
                type="email"
                name="officialEmail"
                value={formData.officialEmail}
                onChange={handleInputChange}
                placeholder="you@company.com"
                className={`w-full px-4 py-3.5 bg-white border ${errors.officialEmail ? "border-red-400 ring-1 ring-red-200" : "border-[#E2E8F0] focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/20"} rounded-xl text-[#1E293B] placeholder:text-[#94A3B8] text-sm font-medium transition-all focus:outline-none`}
              />
              {errors.officialEmail && (
                <p className="text-red-500 text-xs mt-1.5 ml-1">{errors.officialEmail}</p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-[#FF6B35] hover:bg-[#e85f2c] disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-4 rounded-2xl text-base shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-2 mt-2"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Submitting...
                </span>
              ) : (
                <>
                  Continue
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
